import { AssetOptionsContext } from '@contexts/asset-options'
import { withDisplayName } from '@hocs/with-display-name'
import MediaPlayer from '@ui/media/media-player'
import { graphql } from 'gatsby'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import React, { useContext, useMemo } from 'react'

type AssetProps = Partial<Omit<Queries.AssetFragment, 'file'>> & {
  file?: Partial<Omit<Queries.AssetFragment['file'], 'url' | 'contentType'>> & {
    url: string
    contentType: string
  }
}

const ContentfulAsset: Utils.EntryFC<AssetProps> = ({ content }) => {
  const {
    width,
    height,
    maxHeight,
    maxWidth,
    objectFit,
    alt,
    minSvgWidth,
    maxSvgHeight,
    objectPosition,
    className,
    loading,
    ...rest
  } = useContext(AssetOptionsContext)
  if (!content.file) {
    return null
  }

  const { title, file: { contentType, url, details } = {} } = content
  const assetWidth = width || details?.image?.width || '100%'
  const assetHeight = height || details?.image?.height || '100%'
  const assetAlt = alt || title || ''
  const assetMaxWidth = maxWidth || 'none'
  const loadingState = loading || undefined

  const styleProps = useMemo(
    () => ({
      width: assetWidth,
      height: assetHeight,
      maxHeight,
      maxWidth: assetMaxWidth,
    }),
    [assetWidth, assetHeight, maxHeight, assetMaxWidth]
  )

  switch (contentType) {
    case 'image/gif':
    case 'image/svg+xml': {
      return (
        <img
          alt={assetAlt}
          src={url}
          style={{
            width,
            height: assetHeight,
            minWidth: minSvgWidth,
            maxHeight: maxSvgHeight,
            maxWidth: assetMaxWidth,
            objectFit,
            objectPosition,
            ...rest,
          }}
        />
      )
    }

    case 'image/avif':
    case 'image/webp':
    case 'image/png':
    case 'image/jpeg': {
      const image = getImage(content)
      return image ? (
        <div className="h-full w-full">
          <GatsbyImage
            alt={assetAlt}
            className={className}
            image={image}
            loading={loadingState}
            objectFit={objectFit}
            style={styleProps}
          />
        </div>
      ) : null
    }

    case 'video/mp4': {
      const options = {
        format: 'video/mp4',
        width: assetWidth,
        height: assetHeight,
      }

      return <MediaPlayer options={options} url={url} />
    }

    default: {
      return <></>
    }
  }
}

export const query = graphql`
  fragment ContentfulDefaultAsset on ContentfulAsset {
    ...Asset
    gatsbyImageData(placeholder: BLURRED, formats: [AVIF, WEBP, AUTO])
  }

  fragment ContentfulBaseAsset on ContentfulAsset {
    ...ContentfulAsset
    description
    gatsbyImageData(placeholder: BLURRED, formats: [AVIF, WEBP, AUTO])
  }

  fragment ContentfulFullWidthAvifAsset on ContentfulAsset {
    ...ContentfulAsset
    description
    gatsbyImageData(layout: FULL_WIDTH, formats: [AVIF, WEBP, AUTO])
  }

  fragment ContentfulAvifAsset on ContentfulAsset {
    ...ContentfulAsset
    description
    gatsbyImageData(formats: [AVIF, WEBP, AUTO])
  }

  fragment Asset on ContentfulAsset {
    ...ContentfulAsset
    title
    description
    file {
      url
      fileName
      contentType
      details {
        image {
          height
          width
        }
      }
    }
  }
`

export default withDisplayName(ContentfulAsset, 'ContentfulAsset')
